import { useState, useCallback, useRef } from 'react';

import { useActions } from '../../../shared/common/hooks/useActions';
import { useTypedSelector } from '../../../shared/common/hooks/useTypedSelector';
import { useTimerCleanup } from '../../../shared/common/hooks/useTimerCleanup';
import {
  DashboardComponent,
  EditBlockState,
  BlockVisible,
  FullReportModalState,
  BlockComponentType,
} from '../types';
import { calculateNewBlockY } from '../utils/layoutMigration';

// ==================== Хук ====================

/**
 * Хук для управления блоками дашборда.
 * Содержит логику:
 * - Добавления, редактирования, удаления, клонирования блоков
 * - Управления модальными окнами (add, edit, fullReport)
 * - Очистки Redux state при удалении
 * - Счётчика обновлений (updateTrigger)
 *
 * Оптимизации:
 * - Использует ref для чтения dashboardComponents (стабильные callback-ы)
 * - setState callback form для handleAddBlock и handleEditBlock
 */
export const useDashboardBlocks = (
  dashboardComponents: DashboardComponent[],
  setDashboardComponents: React.Dispatch<React.SetStateAction<DashboardComponent[]>>,
  pushSnapshot?: () => void
) => {
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const currentDashboard = useTypedSelector((state) => state.reports.currentDashboard);

  const {
    setCurrentDashboard,
    setMetaTableFetching,
    setMetaChartFetching,
    clearBlockFilters,
    clearBlockTableState,
    clearBlockChartState,
    clearBlockMetaState,
    clearBlockAttributes,
    clearBlockDetails,
  } = useActions();

  const { safeSetTimeout } = useTimerCleanup();

  // Ref для стабильного доступа к актуальным компонентам
  const componentsRef = useRef(dashboardComponents);
  componentsRef.current = dashboardComponents;

  // Текущее состояние таблиц блоков: «%», спарклайны, заливку и «Динамику» могли поменять
  // после последнего сохранения — копия блока берёт их отсюда, как окно «Редактировать»
  const tableBlocks = useTypedSelector((state) => state.table.blocks);
  const tableBlocksRef = useRef(tableBlocks);
  tableBlocksRef.current = tableBlocks;

  // ==================== Модальные состояния ====================

  const [addBlockVisible, setAddBlockVisible] = useState<BlockVisible>({
    visible: false,
    blockType: '',
  });

  const [editBlockState, setEditBlockState] = useState<EditBlockState>({
    visible: false,
    id: '',
    data: {},
  });

  const [fullReportModalState, setFullReportModalState] = useState<FullReportModalState>({
    visible: false,
  });

  // Счётчик обновлений для триггера перезагрузки данных блоков
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // ==================== Обработчики ====================

  /**
   * Добавить новый блок.
   * Автоматически размещает блок ниже всех существующих.
   */
  const handleAddBlock = useCallback(
    (newComp: DashboardComponent) => {
      pushSnapshot?.();
      const maxY = calculateNewBlockY(componentsRef.current);
      const newCompWithPosition: DashboardComponent = {
        ...newComp,
        layoutData: {
          ...newComp.layoutData,
          y: maxY,
        },
      };
      const newDashboardComponents = [...componentsRef.current, newCompWithPosition];
      setDashboardComponents(newDashboardComponents);
      if (selectedSchool?.id) {
        const values = { ...currentDashboard.values, blocks: newDashboardComponents };
        setCurrentDashboard({ ...currentDashboard, values });
      }
    },
    [selectedSchool?.id, currentDashboard, setCurrentDashboard, setDashboardComponents, pushSnapshot]
  );

  /**
   * Редактировать блок.
   * Обновляет данные блока и триггерит перезагрузку.
   */
  const handleEditBlock = useCallback(
    (updComp: DashboardComponent) => {
      pushSnapshot?.();
      const prevState = componentsRef.current;
      const updCompIndex = prevState.findIndex(
        (comp) => comp.data.blockId === updComp.data.blockId
      );
      if (updCompIndex !== -1) {
        const newDashboardComponents = [...prevState];
        newDashboardComponents[updCompIndex] = updComp;
        setDashboardComponents(newDashboardComponents);
        if (selectedSchool?.id) {
          const values = { ...currentDashboard.values, blocks: newDashboardComponents };
          setCurrentDashboard({ ...currentDashboard, values });
        }
      }

      // Двухфазный триггер загрузки: сброс → safeSetTimeout → установка
      // Необходим потому что Form.onFinish работает через Promise.then (async),
      // и в React 17 dispatch-ы не батчатся — старые passive effects могут
      // "потребить" флаг isChartFetching раньше нового useEffect.
      const blockId = updComp.data.blockId;
      if (updComp.componentType === 'table' || updComp.componentType === 'number') {
        setMetaTableFetching({ value: false, blockId });
        safeSetTimeout(() => {
          setMetaTableFetching({ value: true, blockId });
        }, 0);
      } else {
        setMetaChartFetching({ value: false, blockId });
        // Также сбрасываем/триггерим table fetching для chart-блоков (pie/bar/treemap зависят от tableData)
        setMetaTableFetching({ value: false, blockId });
        safeSetTimeout(() => {
          setMetaChartFetching({ value: true, blockId });
          setMetaTableFetching({ value: true, blockId });
        }, 0);
      }
      setUpdateTrigger((prev) => prev + 1);
    },
    [selectedSchool?.id, currentDashboard, setCurrentDashboard, setMetaTableFetching, setMetaChartFetching, safeSetTimeout, pushSnapshot]
  );

  /**
   * Удалить блок.
   * Использует ref для доступа к компонентам, чтобы не зависеть от dashboardComponents.
   * Очищает все связанные Redux-состояния блока.
   */
  const handleDelete = useCallback(
    (id: string) => {
      pushSnapshot?.();
      const current = componentsRef.current;
      const componentToDelete = current.find(
        (component) => component.layoutData.i === id
      );
      const blockId = componentToDelete?.data?.blockId;

      const newDashboardComponents = current.filter(
        (component) => component.layoutData.i !== id
      );
      setDashboardComponents(newDashboardComponents);

      if (selectedSchool?.id) {
        const values = { ...currentDashboard.values, blocks: newDashboardComponents };
        setCurrentDashboard({ ...currentDashboard, values });
      }

      if (blockId) {
        clearBlockFilters(blockId);
        clearBlockTableState(blockId);
        clearBlockChartState(blockId);
        clearBlockMetaState(blockId);
        clearBlockAttributes(blockId);
        clearBlockDetails(blockId);
      }
    },
    [
      selectedSchool?.id,
      currentDashboard,
      setCurrentDashboard,
      clearBlockFilters,
      clearBlockTableState,
      clearBlockChartState,
      clearBlockMetaState,
      clearBlockAttributes,
      clearBlockDetails,
      pushSnapshot,
    ]
  );

  /**
   * Открыть модалку редактирования.
   */
  const handleEdit = useCallback(
    (id: string) => {
      const componentData = componentsRef.current.find(
        (component) => component.layoutData.i === id
      );
      setEditBlockState({
        id,
        visible: true,
        data: componentData || {},
      });
    },
    []
  );

  /**
   * Клонировать блок — открыть модалку добавления с предзаполненными данными.
   */
  const handleClone = useCallback(
    (id: string) => {
      const componentToClone = componentsRef.current.find(
        (component) => component.layoutData.i === id
      );
      if (!componentToClone) return;

      const blockType = componentToClone.componentType as BlockComponentType;
      const cloneBlockId = componentToClone.data?.blockId;
      const cloneTableState = cloneBlockId ? tableBlocksRef.current?.[cloneBlockId] : undefined;

      if (blockType === 'heading') {
        setAddBlockVisible({
          visible: true,
          blockType: 'heading',
          copyData: {
            title: componentToClone.title,
            heading: componentToClone.data?.heading || componentToClone.title,
            headingStyles: componentToClone.data?.headingStyles || {
              fontSize: 20,
              fontWeight: 800,
              color: '#111827',
            },
            description: componentToClone.data?.description,
          },
        });
        return;
      }

      setAddBlockVisible({
        visible: true,
        blockType: blockType,
        copyData: {
          title: componentToClone.title,
          description: componentToClone.data?.description,
          date: componentToClone.data?.date,
          details: componentToClone.data?.details,
          attribution: componentToClone.data?.attribution,
          chartType: componentToClone.data?.chartType,
          selectedMetrics: componentToClone.data?.selectedMetrics || componentToClone.data?.metrics,
          selectedGroupings: componentToClone.data?.selectedGroupings || componentToClone.data?.groupings,
          metricsChecked: componentToClone.data?.metricsChecked,
          groupsChecked: componentToClone.data?.groupsChecked,
          totalChecked: componentToClone.data?.totalChecked,
          percentages: cloneTableState?.percentages ?? componentToClone.data?.percentages,
          sparklines: cloneTableState?.sparklines ?? componentToClone.data?.sparklines,
          cellFills: cloneTableState?.cellFills ?? componentToClone.data?.cellFills,
          analysis: cloneTableState?.analysis ?? componentToClone.data?.analysis,
          cellFilling: cloneTableState?.cellFilling ?? componentToClone.data?.cellFilling,
          filters: componentToClone.data?.filters,
          filterParent: componentToClone.data?.filterParent,
          filtersTable: componentToClone.data?.filtersTable,
          sortTable: componentToClone.data?.sortTable,
          groupsSelectionMode: componentToClone.data?.groupsSelectionMode,
          selectFirstNRows: componentToClone.data?.selectFirstNRows,
          colorRules: componentToClone.data?.colorRules,
        },
      });
    },
    []
  );

  /**
   * Открыть модалку полного отчёта.
   */
  const handleOpenFullReport = useCallback((component: DashboardComponent) => {
    setFullReportModalState({
      visible: true,
      blockId: component.data.blockId,
      title: component.title,
      date: component.data.date as string,
      componentType: component.componentType,
    });
  }, []);

  /**
   * Закрыть модалку полного отчёта.
   */
  const handleCloseFullReport = useCallback(() => {
    setFullReportModalState({ visible: false });
  }, []);

  /**
   * Принудительное обновление (инкремент updateTrigger).
   */
  const forceUpdate = useCallback(() => {
    setUpdateTrigger((prev) => prev + 1);
  }, []);

  return {
    // Модальные состояния
    addBlockVisible,
    setAddBlockVisible,
    editBlockState,
    setEditBlockState,
    fullReportModalState,
    updateTrigger,

    // Обработчики блоков
    handleAddBlock,
    handleEditBlock,
    handleDelete,
    handleEdit,
    handleClone,
    handleOpenFullReport,
    handleCloseFullReport,
    forceUpdate,
  };
};

export default useDashboardBlocks;
